const WaterShader = {
    vertexShader: `
        uniform float time;
        uniform float mapSize;
        uniform sampler2D heightMap;
        varying vec3 vWorldPos;
        varying float vDistFromCenter;

        void main() {
            vec3 pos = position;
            vWorldPos = pos;
            vDistFromCenter = length(position.xz);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
    `,

    fragmentShader: `
        uniform float time;
        uniform float islandRadius;
        uniform float mapSize;
        uniform sampler2D heightMap;
        varying vec3 vWorldPos;
        varying float vDistFromCenter;

        float hash(vec2 p) {
            return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }
        float noise(vec2 p) {
            vec2 i = floor(p);
            vec2 f = fract(p);
            f = f * f * (3.0 - 2.0 * f);
            return mix(
                mix(hash(i), hash(i + vec2(1,0)), f.x),
                mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), f.x),
                f.y
            );
        }

        void main() {
            vec2 uv = vWorldPos.xz / mapSize + 0.5;
            float terrainH = texture2D(heightMap, uv).r;
            // Margin to avoid edge clamping artifacts from heightmap texture
            float margin = 3.0 / 128.0;
            bool outsideMap = uv.x < margin || uv.x > (1.0 - margin) || uv.y < margin || uv.y > (1.0 - margin);

            // Override terrainH for outside-map areas (clamp-to-edge gives wrong values)
            if (outsideMap) {
                terrainH = -2.0;
            }

            float t = vDistFromCenter / islandRadius;

            // --- Depth colors ---
            vec3 shoreColor = vec3(0.35, 0.82, 0.92);
            vec3 shallowColor = vec3(0.15, 0.55, 0.90);
            vec3 deepColor = vec3(0.06, 0.22, 0.60);

            float depthFactor;
            if (outsideMap) {
                depthFactor = smoothstep(1.0, 2.5, t);
            } else {
                depthFactor = smoothstep(0.0, -3.0, terrainH);
            }
            vec3 waterColor = mix(shoreColor, shallowColor, depthFactor);
            waterColor = mix(waterColor, deepColor, smoothstep(0.5, 1.0, depthFactor));

            // --- Thin foam along coastline ---
            float texel = 1.0 / 128.0;
            float hL = texture2D(heightMap, uv + vec2(-texel, 0.0)).r;
            float hR = texture2D(heightMap, uv + vec2( texel, 0.0)).r;
            float hU = texture2D(heightMap, uv + vec2(0.0, -texel)).r;
            float hD = texture2D(heightMap, uv + vec2(0.0,  texel)).r;

            float maxNeighbor = max(max(hL, hR), max(hU, hD));
            float coastProximity = 0.0;
            if (!outsideMap && terrainH <= 0.3) {
                coastProximity = smoothstep(0.0, 0.8, maxNeighbor) * smoothstep(-1.5, 0.1, terrainH);
            }
            float foamNoise = noise(vec2(vWorldPos.x * 0.15 + time * 0.06, vWorldPos.z * 0.15 + time * 0.05));
            float foam = coastProximity * step(0.4, foamNoise + coastProximity * 0.2);
            foam *= 0.45;
            waterColor = mix(waterColor, vec3(1.0), foam);

            // --- Subtle caustic highlights ---
            float highlight = sin(vWorldPos.x * 0.05 + time * 0.4) *
                              sin(vWorldPos.z * 0.04 + time * 0.3);
            highlight = pow(max(0.0, highlight), 4.0) * 0.04;
            waterColor += vec3(highlight);

            // Alpha: fully opaque outside map, opaque over water, transparent over land
            float alpha;
            if (outsideMap) {
                alpha = 1.0;
            } else if (terrainH <= 0.3) {
                alpha = 0.95;
            } else if (terrainH < 0.6) {
                alpha = mix(0.95, 0.0, smoothstep(0.3, 0.6, terrainH));
            } else {
                alpha = 0.0;
            }

            gl_FragColor = vec4(waterColor, alpha);
        }
    `,

    uniforms: {
        time: { value: 0 },
        islandRadius: { value: 325.0 },
        heightMap: { value: null },
        mapSize: { value: 650.0 }
    }
};

export default WaterShader;
